'use client'

import * as React from 'react'
import { Heart, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSaveListing } from './use-save-listing'

interface SaveListingButtonProps {
  listingId: string
  className?: string
  variant?: 'icon' | 'button'
  size?: 'sm' | 'md' | 'lg'
}

// Save/unsave button — all logic (auth gating, optimistic updates, planner
// sync, saved-ids sync, per-listing serialization) lives in the shared
// useSaveListing hook; this component is purely presentational.
export function SaveListingButton({
  listingId,
  className,
  variant = 'icon',
  size = 'md'
}: SaveListingButtonProps) {
  const { isSaved, isSaving, toggleSave } = useSaveListing(listingId)

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  }

  const buttonSizeClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2',
  }

  if (variant === 'button') {
    return (
      <button
        onClick={toggleSave}
        disabled={isSaving}
        className={cn(
          'flex items-center gap-2 py-2.5 px-4 rounded-lg',
          'text-sm font-medium transition-colors',
          isSaved
            ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
            : 'bg-muted hover:bg-muted/80 text-muted-foreground',
          isSaving && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        {isSaving ? (
          <Loader2 className={cn(sizeClasses[size], 'animate-spin')} />
        ) : (
          <Heart
            className={cn(sizeClasses[size], isSaved && 'fill-current')}
          />
        )}
        {isSaved ? 'Saved' : 'Save'}
      </button>
    )
  }

  return (
    <button
      onClick={toggleSave}
      disabled={isSaving}
      className={cn(
        buttonSizeClasses[size],
        'rounded-full transition-all',
        isSaved
          ? 'bg-red-500 text-white hover:bg-red-600'
          : 'bg-black/50 text-white hover:bg-black/70',
        isSaving && 'opacity-50 cursor-not-allowed',
        className
      )}
      title={isSaved ? 'Remove from liked' : 'Like listing'}
    >
      {isSaving ? (
        <Loader2 className={cn(sizeClasses[size], 'animate-spin')} />
      ) : (
        <Heart
          className={cn(sizeClasses[size], isSaved && 'fill-current')}
        />
      )}
    </button>
  )
}
